import React, { useState, useRef, useEffect } from "react";
import { GoogleMap, Polygon } from "@react-google-maps/api";
import { useSelector } from "react-redux";
import axiosInstance from "../../../../configuration/request";
import CustomButton from "../../../../component/elements/CustomButton/CustomButton";
import CloseIcon from "@mui/icons-material/Close";
import TextFieldCustom from "../../../../component/elements/textfieldCustom";
import { Snackbar } from "@mui/material";
import { useSnackbar } from "../../../../component/SnackbarContext";
import { useNavigate } from "react-router-dom";
import StripUnauthorised from "../../../../component/stripeUnauthorised";

const containerStyle = {
  width: "100%",
  height: "600px",
  border: "1px solid #ccc",
  borderRadius: "8px",
};

const defaultCenter = {
  lat: 42.3601,
  lng: -71.0589,
};

const polygonOptions = {
  fillOpacity: 0.3,
  strokeOpacity: 0.8,
  strokeWeight: 2,
};

const getColor = (index) => {
const colors = [
  "#007BFF", 
  "#1E90FF", 
  "#00BFFF", 
  "#3399FF",
  "#66CCFF", 
  "#00CED1",
];

  return colors[index % colors.length];
};

const ServiceAreaManagement = () => {
  const showSnackbar = useSnackbar();
  const mapRef = useRef(null);
  const [boundaries, setBoundaries] = useState([]);
  const [pendingAreas, setPendingAreas] = useState([]);
  const [routingNumber, setRoutingNumber] = useState("");
  const { loginDetails } = useSelector((state) => state.counter);
  const storeId = loginDetails?.storeId;
    const { isStripeAccount } = useSelector((state) => state.counter);
  const navigate = useNavigate();

  useEffect(() => {
    fetchServiceAreas();
  }, [storeId]);

  const fetchServiceAreas = async () => {
    if (!storeId) return;
    try {
      const response = await axiosInstance.get(`/customer/store/service-areas/${storeId}`);
      const parsed = (response.data || []).map(area => ({
        routingNumber: area.routingNumber,
        path: area.coordinates.map(c => ({
          lat: parseFloat(c.lat),
          lng: parseFloat(c.long),
        })),
      }));
      setBoundaries(parsed);
    } catch (err) {
      console.error("Failed to fetch service areas", err);
    }
  };

  const zoomToPolygon = (path) => {
    if (!mapRef.current || !path.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    path.forEach(pt => bounds.extend(pt));
    mapRef.current.fitBounds(bounds);
  };

  const handleZipChange = async (e) => {
    const zip = e.target.value.trim();
    setRoutingNumber(zip);

    if (zip.length === 5 && !pendingAreas.some(p => p.routingNumber === zip)) {
      try {
        const { data } = await axiosInstance.get(`/customer/users/zip-coordinated?zip=${zip}`);
        if (data?.coordinates?.length === 4) {
          // Sort in polygon order: bottomLeft → topLeft → topRight → bottomRight
          const getByName = (name) => data.coordinates.find(c => c.name === name);
          const path = [
            getByName("bottomLeft"),
            getByName("topLeft"),
            getByName("topRight"),
            getByName("bottomRight"),
          ].map(coord => ({
            lat: parseFloat(coord.lat),
            lng: parseFloat(coord.lng),
          }));
          const newEntry = { routingNumber: zip, path };
          setPendingAreas(prev => [...prev, newEntry]);
          zoomToPolygon(path);
          setRoutingNumber("");
        } else {
          showSnackbar("Bounds not available for this Zipcode", "error")
        }
      } catch (err) {
        showSnackbar("Failed to fetch zipcode bounds", err);
      }
    }
  };

  const removePendingArea = (indexToRemove) => {
    setPendingAreas(prev => prev.filter((_, i) => i !== indexToRemove));
  };

const handleSaveRouting = async () => {
  if (!pendingAreas.length || !storeId) return;

  // Merge boundaries (existing) + pendingAreas (new)
  const combinedAreas = [...boundaries, ...pendingAreas];
  if(combinedAreas.length <= 4){
      try {
    await axiosInstance.patch(`/customer/store/service-areas/update/${storeId}`, {
      serviceAreas: combinedAreas.map(b => ({
        routingNumber: b.routingNumber,
        coordinates: b.path.map(pt => ({
          lat: pt.lat.toString(),
          long: pt.lng.toString(),
        })),
      })),
    });
    setPendingAreas([]);
    fetchServiceAreas(); // Refresh saved areas from backend
  } catch (err) {
    console.error("Failed to update service areas", err);
  }
  }else{
    showSnackbar("You can add up to 4 service areas only.", "info");
  }

};


  const handleRemoveArea = async (indexToRemove) => {
    const updatedBoundaries = boundaries.filter((_, i) => i !== indexToRemove);
    setBoundaries(updatedBoundaries);
    try {
      await axiosInstance.patch(`/customer/store/service-areas/update/${storeId}`, {
        serviceAreas: updatedBoundaries.map(b => ({
          routingNumber: b.routingNumber,
          coordinates: b.path.map(pt => ({
            lat: pt.lat.toString(),
            long: pt.lng.toString(),
          })),
        })),
      });
    } catch (err) {
      console.error("Failed to update service areas after deletion", err);
    }
  };

  return (
   <div>

      
      {!isStripeAccount &&     <div className="pl-8 pr-6">
      <h2 className="text-2xl font-semibold mb-2 mt-1 ml-1">Service Area Management</h2>
      <StripUnauthorised handleBackProfile={() => navigate('/app/business/profile')} />
    </div>}
      {isStripeAccount && ( 
         <div className="space-y-6 px-6 py-6 h-[calc(100vh-100px)] overflow-y-auto scrollbar-hide">

      {/* ZIP input */}
      <div className="p-5 rounded border border-blue-200 shadow-md">
        <h2 className="text-xl font-semibold mb-3">Add Service Area by Zip Code</h2>
        <TextFieldCustom
          rows={1}
          fullWidth
          multiline
          heightClass="serviceArea"
          value={routingNumber}
          onChange={handleZipChange}
          placeholder="Enter Zip Code (e.g., 02108)"
        />
      </div>

      {/* Pending areas */}
      {pendingAreas.length > 0 && (
        <div className="p-3 mt-4 rounded border border-dashed border-blue-300">
          <h2 className="font-medium mb-2">Pending Areas:</h2>
          <div className="flex flex-wrap gap-3">
            {pendingAreas.map((item, idx) => (
              <div key={`${item.routingNumber}-${idx}`} className="relative w-fit">
                <CustomButton
                  customeClass="primary-custom-button w-[100px] h-[40px] text-blue-600 pr-6"
                  onClick={() => zoomToPolygon(item.path)}
                >
                  {item.routingNumber}
                </CustomButton>
                <button
                  onClick={() => removePendingArea(idx)}
                  className="absolute top-[-8px] right-[-6px] bg-white text-red-500 rounded-full hover:text-red-700"
                  title="Remove area"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            ))}
          </div>
          <div className="flex justify-end mt-4 gap-3">
            <CustomButton
              onClick={() => setPendingAreas([])}
              customeClass="w-[100px] h-[40px] primary-custom-button"
            >
              Cancel
            </CustomButton>
            <CustomButton
              onClick={handleSaveRouting}
              customeClass="w-[100px] h-[40px] gradient-button"
            >
              Save
            </CustomButton>
          </div>
        </div>
      )}

      {/* Saved areas */}
      {boundaries.length > 0 && (
        <div className="p-3 rounded shadow">
          <h2 className="font-semibold mb-2">Added Areas:</h2>
          <div className="flex flex-wrap gap-3">
            {boundaries.map((item, idx) => (
              <div key={`${item.routingNumber}-${idx}`} className="relative w-fit">
                <CustomButton
                  customeClass="primary-custom-button w-[100px] h-[40px] text-blue-600 pr-6"
                  onClick={() => zoomToPolygon(item.path)}
                >
                  {item.routingNumber}
                </CustomButton>
                <button
                  onClick={() => handleRemoveArea(idx)}
                  className="absolute top-[-8px] right-[-6px] bg-white text-red-500 rounded-full hover:text-red-700"
                  title="Remove area"
                >
                  <CloseIcon fontSize="small" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Map */}
      <GoogleMap
        mapContainerStyle={containerStyle}
        center={defaultCenter}
        zoom={5}
        onLoad={map => (mapRef.current = map)}
      >
        {[...boundaries, ...pendingAreas].map((item, idx) => (
          <Polygon
            key={`${item.routingNumber}-${idx}`}
            path={item.path}
            options={{
              ...polygonOptions,
              fillColor: getColor(idx),
              strokeColor: getColor(idx),
            }}
          />
        ))}
      </GoogleMap>
    </div>
        )}
   </div>
  );
};

export default ServiceAreaManagement;
